import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { setActiveProfile } from '../utils/profile';
import { Avatar } from '../components/Avatar';
import './school-login.css';

/* Tiny open-book icon for the "Let's read" CTA — stroke colour inherits
 * from the button text so the dark gold reads cleanly on the gradient. */
const IconBook = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 5.5A2.5 2.5 0 0 1 5.5 3H11v16H5.5A2.5 2.5 0 0 1 3 16.5v-11z" />
    <path d="M21 5.5A2.5 2.5 0 0 0 18.5 3H13v16h5.5a2.5 2.5 0 0 0 2.5-2.5v-11z" />
  </svg>
);

/* Kid login flow: school code → class → name → 4-digit PIN → JWT.
 * Each step is a numbered slide. The flow forgives miss-steps — every
 * step has a Back button and resetting the code rewinds the whole flow. */
export default function SchoolLogin() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [code, setCode] = useState('');
  const [school, setSchool] = useState(null);
  const [classes, setClasses] = useState([]);
  const [activeClass, setActiveClass] = useState(null);
  const [students, setStudents] = useState([]);
  const [activeStudent, setActiveStudent] = useState(null);
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const codeInputRef = useRef(null);
  const pinInputRef = useRef(null);

  useEffect(() => {
    if (step === 1 && codeInputRef.current) codeInputRef.current.focus();
    if (step === 4 && pinInputRef.current) pinInputRef.current.focus();
    // Errors are per-step — clear on every step change so a stale
    // message from a previous step never sticks on Back navigation.
    setErr('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const submitCode = async (e) => {
    if (e) e.preventDefault();
    setErr('');
    const trimmed = code.trim().toUpperCase();
    if (trimmed.length !== 6) {
      setErr(trimmed.length === 0
        ? 'Type your school code to start.'
        : `School code is 6 letters and numbers (you have ${trimmed.length}).`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.get(`/api/schools/${encodeURIComponent(trimmed)}/classes`);
      setSchool(data.school);
      setClasses(data.classes || []);
      setStep(2);
    } catch (e2) {
      setErr(e2?.response?.data?.message || 'We couldn\'t find a school with that code.');
    } finally { setBusy(false); }
  };

  // Auto-submit step 1 the instant a valid 6-char code is *typed*.
  // We track the last auto-submitted value in a ref so coming Back to
  // step 1 with the same code already entered does NOT re-fire the
  // submit (otherwise the user can never get back to the code field).
  const lastAutoSubmittedRef = useRef(null);
  useEffect(() => {
    if (step !== 1) return;
    if (code.length !== 6 || busy) return;
    if (lastAutoSubmittedRef.current === code) return;
    const t = setTimeout(() => {
      lastAutoSubmittedRef.current = code;
      submitCode();
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, step]);

  const pickClass = async (cls) => {
    setErr('');
    setBusy(true);
    try {
      const { data } = await axios.get(
        `/api/schools/${encodeURIComponent(school.code)}/classes/${cls._id}/students`
      );
      setActiveClass(cls);
      setStudents(data.students || []);
      setStep(3);
    } catch (e) {
      setErr(e?.response?.data?.message || 'Could not load that class.');
    } finally { setBusy(false); }
  };

  const pickStudent = (s) => {
    setActiveStudent(s);
    setPin('');
    setStep(4);
  };

  const submitPin = async (e) => {
    e.preventDefault();
    setErr('');
    if (!/^\d{4}$/.test(pin)) { setErr('PIN must be 4 digits.'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post('/api/schools/student-login', {
        profileId: activeStudent._id,
        pin,
      });
      if (data?.token) localStorage.setItem('adeeb-token', data.token);
      if (data?.user)  localStorage.setItem('adeeb-user', JSON.stringify(data.user));
      if (data?.profile) setActiveProfile(data.profile);
      window.dispatchEvent(new Event('adeeb-auth-change'));
      navigate('/', { replace: true });
    } catch (e2) {
      setErr(e2?.response?.data?.message || 'Login failed. Try again.');
      setPin('');
      pinInputRef.current?.focus();
    } finally { setBusy(false); }
  };

  const restart = () => {
    lastAutoSubmittedRef.current = null;
    setStep(1); setCode(''); setSchool(null);
    setClasses([]); setActiveClass(null);
    setStudents([]); setActiveStudent(null);
    setPin(''); setErr('');
  };

  return (
    <div className="sl-page">
      <div className="sl-bg" aria-hidden="true" />
      <Link to="/school-portal" className="sl-floating-back">&#8592; School portal</Link>

      <div className="sl-shell">
        <div className="sl-eyebrow">School sign in</div>

        <Stepper step={step} school={school} activeClass={activeClass} activeStudent={activeStudent} />

        {step === 1 && (
          <form className="sl-card sl-step1" onSubmit={submitCode} noValidate>
            <h1 className="sl-h1">What's your school code?</h1>
            <p className="sl-sub">Your teacher will give it to you. It's 6 letters and numbers.</p>
            <input
              ref={codeInputRef}
              type="text"
              inputMode="text"
              autoComplete="off"
              spellCheck={false}
              className="sl-code-input"
              maxLength={6}
              value={code}
              onChange={(e) => { setCode(e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase()); if (err) setErr(''); }}
              placeholder="ABC123"
              aria-label="School code"
            />
            <div className="sl-code-progress" aria-live="polite">
              {code.length < 6
                ? `${code.length} of 6 typed${code.length > 0 ? ` — ${6 - code.length} more` : ''}`
                : 'Looks good ✓'}
            </div>
            {err && <div className="sl-error" role="alert">{err}</div>}
            <button type="submit" className="sl-btn sl-btn-primary" disabled={busy || code.length === 0}>
              {busy ? 'Looking…' : 'Continue'}
            </button>
            <p className="sl-tiny">Not a school user? <Link to="/login" className="sl-link">Parent sign in</Link></p>
          </form>
        )}

        {step === 2 && (
          <div className="sl-card">
            <h1 className="sl-h1">Pick your class</h1>
            <p className="sl-sub">{school?.name}</p>
            {classes.length === 0 ? (
              <div className="sl-empty">No classes yet. Ask your teacher.</div>
            ) : (
              <div className="sl-grid">
                {classes.map((c) => (
                  <button key={c._id} type="button" className="sl-tile" onClick={() => pickClass(c)} disabled={busy}>
                    <span className="sl-tile-emoji" aria-hidden="true">📚</span>
                    <span className="sl-tile-label">{c.name}</span>
                  </button>
                ))}
              </div>
            )}
            {err && <div className="sl-error" role="alert">{err}</div>}
            <div className="sl-actions">
              <button type="button" className="sl-btn sl-btn-ghost" onClick={() => setStep(1)}>&#8592; Back</button>
              <button type="button" className="sl-btn sl-btn-ghost" onClick={restart}>Wrong school</button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="sl-card">
            <h1 className="sl-h1">Which one is you?</h1>
            <p className="sl-sub">{activeClass?.name} · {school?.name}</p>
            {students.length === 0 ? (
              <div className="sl-empty">No students in this class yet. Ask your teacher.</div>
            ) : (
              <div className="sl-grid sl-grid-students">
                {students.map((s) => (
                  <button key={s._id} type="button" className="sl-tile sl-tile-student" onClick={() => pickStudent(s)}>
                    <Avatar value={s.avatar} name={s.name} size={56} className="sl-avatar-img" />
                    <span className="sl-tile-label">{s.name}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="sl-actions">
              <button type="button" className="sl-btn sl-btn-ghost" onClick={() => setStep(2)}>&#8592; Back to classes</button>
            </div>
          </div>
        )}

        {step === 4 && (
          <form className="sl-card sl-step4" onSubmit={submitPin} noValidate>
            <div className="sl-bigavatar-wrap" aria-hidden="true">
              <Avatar value={activeStudent?.avatar} name={activeStudent?.name} size={96} />
            </div>
            <h1 className="sl-h1">Hi {activeStudent?.name}!</h1>
            <p className="sl-sub">Type your 4-digit PIN.</p>
            <input
              ref={pinInputRef}
              type="password"
              inputMode="numeric"
              autoComplete="one-time-code"
              className="sl-pin-input"
              maxLength={4}
              value={pin}
              onChange={(e) => { setPin(e.target.value.replace(/\D/g, '').slice(0, 4)); if (err) setErr(''); }}
              aria-label="PIN"
            />
            {err && <div className="sl-error" role="alert">{err}</div>}
            <button type="submit" className="sl-btn sl-btn-primary" disabled={busy || pin.length !== 4}>
              {busy ? 'Signing in…' : <><IconBook /> Let&apos;s read</>}
            </button>
            <div className="sl-actions">
              <button type="button" className="sl-btn sl-btn-ghost" onClick={() => { setStep(3); setPin(''); setErr(''); }}>&#8592; Not me</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/* Icons for each step. Stroke colour inherits from the dot's color so
 * each state (todo/now/done) tints automatically. Square viewBoxes
 * keep them visually balanced inside the 28px circle. */
const StepIcons = [
  // Step 1: school code → key
  () => (
    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="15" r="4" />
      <path d="M11 12l8-8m-2 0h2v2m-3 3l1.5 1.5" />
    </svg>
  ),
  // Step 2: class → group of people
  () => (
    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 20v-2a4 4 0 0 0-3-3.87" />
      <path d="M9 20v-2a4 4 0 0 1 4-4h0a4 4 0 0 1 4 4" />
      <circle cx="9" cy="7" r="3" />
      <path d="M16 3.13a3 3 0 0 1 0 5.74" />
    </svg>
  ),
  // Step 3: name → single person
  () => (
    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  // Step 4: PIN → lock
  () => (
    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  ),
];
const IconCheck = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="5 12 10 17 19 7" />
  </svg>
);

function Stepper({ step, school, activeClass, activeStudent }) {
  const labels = useMemo(() => ([
    school?.code   ? school.code   : 'Code',
    activeClass    ? activeClass.name : 'Class',
    activeStudent  ? activeStudent.name : 'Name',
    'PIN',
  ]), [school, activeClass, activeStudent]);
  return (
    <ol className="sl-stepper" aria-label="Progress">
      {labels.map((label, i) => {
        const n = i + 1;
        const state = n < step ? 'done' : n === step ? 'now' : 'todo';
        const Icon = StepIcons[i];
        return (
          <li key={n} className={`sl-step sl-step-${state}`}>
            <span className="sl-step-dot">
              {state === 'done' ? <IconCheck /> : <Icon />}
            </span>
            <span className="sl-step-label">{label}</span>
          </li>
        );
      })}
    </ol>
  );
}

function initialsOf(name) {
  const t = String(name || '').trim();
  if (!t) return '?';
  const parts = t.split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || t[0].toUpperCase();
}
